/**
 * AccouPro — Universal Accounting Diagnostics & Safe Repair Engine
 * 
 * Provides deterministic inspection, validation, and idempotent repair of company charts of accounts,
 * statutory GST ledgers, operating accounts, and ledger group mappings.
 * 
 * Strictly non-destructive: Never alters posted financial transactions, historical balances, or tax returns.
 */

import { Ledger, LedgerGroup, LedgerNature, VoucherType } from "./types";
import { Company } from "@/contexts/CompanyContext";

export interface DiagnosticProposedAction {
  action: "create_ledger" | "update_ledger_group" | "update_company_gst";
  ledgerId?: string;
  ledgerName: string;
  group: LedgerGroup;
  nature: LedgerNature;
  type: "Dr" | "Cr";
  openingBalance: number;
  reason: string;
}

export interface DiagnosticIssue {
  id: string;
  code: string;
  title: string;
  description: string;
  severity: "error" | "warning" | "info";
  category: "duties_and_taxes" | "sales_purchase" | "liquid_accounts" | "equity" | "group_mapping" | "company_gst";
  impact: string;
  canAutoRepair: boolean;
  proposedActions: DiagnosticProposedAction[];
}

export interface DiagnosticReport {
  companyId: string;
  companyName: string;
  isGstRegistered: boolean;
  hasErrors: boolean;
  issues: DiagnosticIssue[];
  autoRepairableCount: number;
  summary: string;
}

// Canonical Definitions for Required Indian Accounting Ledgers
export const STANDARD_REQUIRED_LEDGERS = {
  // Duties & Taxes (Input Tax Credit - Purchases / Inward Supply)
  CGST_IN: {
    name: "CGST Input (9%)",
    group: "Duties & Taxes" as LedgerGroup,
    nature: "Asset" as LedgerNature,
    type: "Dr" as const,
    aliases: ["cgst input", "cgst in", "cgst input (itc)", "input cgst"],
    description: "Central GST input tax credit on purchases"
  },
  SGST_IN: {
    name: "SGST Input (9%)",
    group: "Duties & Taxes" as LedgerGroup,
    nature: "Asset" as LedgerNature,
    type: "Dr" as const,
    aliases: ["sgst input", "sgst in", "sgst input (itc)", "input sgst"],
    description: "State GST input tax credit on purchases"
  },
  IGST_IN: {
    name: "IGST Input (18%)",
    group: "Duties & Taxes" as LedgerGroup,
    nature: "Asset" as LedgerNature,
    type: "Dr" as const,
    aliases: ["igst input", "igst in", "igst input (itc)", "input igst"],
    description: "Integrated GST input tax credit on purchases"
  },

  // Duties & Taxes (Output Tax Liability - Sales / Outward Supply)
  CGST_OUT: {
    name: "CGST Output (9%)",
    group: "Duties & Taxes" as LedgerGroup,
    nature: "Liability" as LedgerNature,
    type: "Cr" as const,
    aliases: ["cgst output", "cgst out", "cgst output (sales)", "output cgst"],
    description: "Central GST liability on sales"
  },
  SGST_OUT: {
    name: "SGST Output (9%)",
    group: "Duties & Taxes" as LedgerGroup,
    nature: "Liability" as LedgerNature,
    type: "Cr" as const,
    aliases: ["sgst output", "sgst out", "sgst output (sales)", "output sgst"],
    description: "State GST liability on sales"
  },
  IGST_OUT: {
    name: "IGST Output (18%)",
    group: "Duties & Taxes" as LedgerGroup,
    nature: "Liability" as LedgerNature,
    type: "Cr" as const,
    aliases: ["igst output", "igst out", "igst output (sales)", "output igst"],
    description: "Integrated GST liability on sales"
  },

  // Operating Accounts
  PURCHASE: {
    name: "Purchase Account",
    group: "Purchase Accounts" as LedgerGroup,
    nature: "Expense" as LedgerNature,
    type: "Dr" as const,
    aliases: ["purchase", "purchases", "purchase account"],
    description: "Standard trading purchase account"
  },
  SALES: {
    name: "Sales Account",
    group: "Sales Accounts" as LedgerGroup,
    nature: "Income" as LedgerNature,
    type: "Cr" as const,
    aliases: ["sales", "sales account", "revenue - sales"],
    description: "Standard trading sales revenue account"
  },
  ROUNDOFF: {
    name: "Round Off",
    group: "Indirect Expenses" as LedgerGroup,
    nature: "Expense" as LedgerNature,
    type: "Dr" as const,
    aliases: ["round off", "roundoff", "rounding adjustment"],
    description: "Invoice round-off adjustment ledger"
  },

  // Inventory Asset & COGS (Perpetual Inventory)
  STOCK_ASSET: {
    name: "Stock-in-Hand / Inventory Asset",
    group: "Stock-in-Hand" as LedgerGroup,
    nature: "Asset" as LedgerNature,
    type: "Dr" as const,
    aliases: ["stock-in-hand", "inventory asset", "stock in hand", "inventory"],
    description: "Balance sheet asset for physical goods stock"
  },
  COGS: {
    name: "Cost of Goods Sold (COGS)",
    group: "Direct Expenses" as LedgerGroup,
    nature: "Expense" as LedgerNature,
    type: "Dr" as const,
    aliases: ["cost of goods sold", "cogs", "cost of sales"],
    description: "Expense recognized when physical inventory is sold"
  },

  // Liquid & Capital Accounts
  CASH: {
    name: "Cash-in-Hand",
    group: "Cash-in-Hand" as LedgerGroup,
    nature: "Asset" as LedgerNature,
    type: "Dr" as const,
    aliases: ["cash", "petty cash", "cash in hand", "cash-in-hand"],
    description: "Liquid physical cash account"
  },
  BANK: {
    name: "Bank Account",
    group: "Bank Accounts" as LedgerGroup,
    nature: "Asset" as LedgerNature,
    type: "Dr" as const,
    aliases: ["bank", "current account", "savings account", "primary bank account"],
    description: "Primary business bank account"
  },
  CAPITAL: {
    name: "Capital Account",
    group: "Capital Account" as LedgerGroup,
    nature: "Equity" as LedgerNature,
    type: "Cr" as const,
    aliases: ["capital", "share capital", "equity capital", "partner's capital"],
    description: "Business owner / equity capital"
  }
};

/**
 * Matches an existing ledger against aliases and canonical group
 */
export function findMatchingLedger(
  ledgers: Ledger[],
  spec: { aliases: string[]; group?: LedgerGroup }
): Ledger | undefined {
  const normalizedAliases = spec.aliases.map(a => a.toLowerCase().trim());
  
  // 1. Try exact or alias match within specified group
  if (spec.group) {
    const inGroup = ledgers.find(l => {
      if (l.group !== spec.group) return false;
      const lower = l.name.toLowerCase().trim();
      return normalizedAliases.some(alias => lower.includes(alias) || alias.includes(lower));
    });
    if (inGroup) return inGroup;
  }

  // 2. Try match anywhere in the ledgers
  return ledgers.find(l => {
    const lower = l.name.toLowerCase().trim();
    return normalizedAliases.some(alias => lower === alias || lower.includes(alias));
  });
}

/**
 * Specifically diagnoses transaction prerequisites for GST Sales and Purchase Invoices
 */
export function diagnoseInvoicePrerequisites(params: {
  company: Company | null;
  ledgers: Ledger[];
  type: VoucherType;
  hasGstItems: boolean;
  isInterState: boolean;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
}): DiagnosticReport {
  const { company, ledgers, type, hasGstItems, isInterState, cgstAmount, sgstAmount, igstAmount } = params;
  const companyId = company?.id || "";
  const companyName = company?.name || "Active Company";
  const isGstRegistered = Boolean(company?.gstRegistered || company?.gstin);
  const issues: DiagnosticIssue[] = [];

  const isSales = type === "Sales" || type === "Credit Note";
  const isPurchase = type === "Purchase" || type === "Debit Note";

  // 1. Operating Account Check (Sales / Purchase)
  if (isPurchase) {
    const purchaseLedger = findMatchingLedger(ledgers, STANDARD_REQUIRED_LEDGERS.PURCHASE);
    if (!purchaseLedger) {
      issues.push({
        id: "err-missing-purchase-ledger",
        code: "MISSING_PURCHASE_ACCOUNT",
        title: "Missing Purchase Account Ledger",
        description: "The Chart of Accounts does not contain a Purchase Account under 'Purchase Accounts'. Required to debit taxable purchase totals.",
        severity: "error",
        category: "sales_purchase",
        impact: "Cannot post GST Purchase Invoices or Bills.",
        canAutoRepair: true,
        proposedActions: [{
          action: "create_ledger",
          ledgerName: STANDARD_REQUIRED_LEDGERS.PURCHASE.name,
          group: STANDARD_REQUIRED_LEDGERS.PURCHASE.group,
          nature: STANDARD_REQUIRED_LEDGERS.PURCHASE.nature,
          type: STANDARD_REQUIRED_LEDGERS.PURCHASE.type,
          openingBalance: 0,
          reason: "Standard trading purchase account under Purchase Accounts"
        }]
      });
    }
  } else if (isSales) {
    const salesLedger = findMatchingLedger(ledgers, STANDARD_REQUIRED_LEDGERS.SALES);
    if (!salesLedger) {
      issues.push({
        id: "err-missing-sales-ledger",
        code: "MISSING_SALES_ACCOUNT",
        title: "Missing Sales Account Ledger",
        description: "The Chart of Accounts does not contain a Sales Account under 'Sales Accounts'. Required to credit taxable sales totals.",
        severity: "error",
        category: "sales_purchase",
        impact: "Cannot post GST Sales Invoices.",
        canAutoRepair: true,
        proposedActions: [{
          action: "create_ledger",
          ledgerName: STANDARD_REQUIRED_LEDGERS.SALES.name,
          group: STANDARD_REQUIRED_LEDGERS.SALES.group,
          nature: STANDARD_REQUIRED_LEDGERS.SALES.nature,
          type: STANDARD_REQUIRED_LEDGERS.SALES.type,
          openingBalance: 0,
          reason: "Standard trading sales revenue account under Sales Accounts"
        }]
      });
    }
  }

  // 2. GST Statutory Ledgers Check (If invoice has GST or company is GST registered)
  if (hasGstItems || cgstAmount > 0 || sgstAmount > 0 || igstAmount > 0 || isGstRegistered) {
    // Check Company GST registration consistency
    if (!company?.gstRegistered && (hasGstItems || cgstAmount > 0 || igstAmount > 0)) {
      issues.push({
        id: "warn-company-gst-disabled",
        code: "COMPANY_GST_FLAG_DISABLED",
        title: "GST Transactions Enabled for Non-GST Profile",
        description: "The company profile has GST Registration disabled, but this transaction contains GST tax lines.",
        severity: "warning",
        category: "company_gst",
        impact: "Invoices will calculate GST but GSTR return reporting requires active GST registration.",
        canAutoRepair: true,
        proposedActions: [{
          action: "update_company_gst",
          ledgerName: "",
          group: "Duties & Taxes",
          nature: "Liability",
          type: "Cr",
          openingBalance: 0,
          reason: "Enable GST registered flag on company settings"
        }]
      });
    }

    if (isPurchase) {
      // Purchase requires Input Tax Ledgers (Asset / Dr)
      const cgstIn = findMatchingLedger(ledgers, STANDARD_REQUIRED_LEDGERS.CGST_IN);
      const sgstIn = findMatchingLedger(ledgers, STANDARD_REQUIRED_LEDGERS.SGST_IN);
      const igstIn = findMatchingLedger(ledgers, STANDARD_REQUIRED_LEDGERS.IGST_IN);

      if ((!isInterState && (cgstAmount > 0 || sgstAmount > 0)) || (!cgstIn || !sgstIn)) {
        if (!cgstIn || !sgstIn) {
          const missingActions: DiagnosticProposedAction[] = [];
          if (!cgstIn) {
            missingActions.push({
              action: "create_ledger",
              ledgerName: STANDARD_REQUIRED_LEDGERS.CGST_IN.name,
              group: STANDARD_REQUIRED_LEDGERS.CGST_IN.group,
              nature: STANDARD_REQUIRED_LEDGERS.CGST_IN.nature,
              type: STANDARD_REQUIRED_LEDGERS.CGST_IN.type,
              openingBalance: 0,
              reason: "Central GST Input Tax Credit ledger under Duties & Taxes (Asset / Dr)"
            });
          }
          if (!sgstIn) {
            missingActions.push({
              action: "create_ledger",
              ledgerName: STANDARD_REQUIRED_LEDGERS.SGST_IN.name,
              group: STANDARD_REQUIRED_LEDGERS.SGST_IN.group,
              nature: STANDARD_REQUIRED_LEDGERS.SGST_IN.nature,
              type: STANDARD_REQUIRED_LEDGERS.SGST_IN.type,
              openingBalance: 0,
              reason: "State GST Input Tax Credit ledger under Duties & Taxes (Asset / Dr)"
            });
          }

          issues.push({
            id: "err-missing-cgst-sgst-in",
            code: "MISSING_CGST_SGST_INPUT_LEDGER",
            title: "Missing CGST / SGST Input Tax Ledgers in Duties & Taxes",
            description: "Intra-state purchases require CGST and SGST Input ledgers under 'Duties & Taxes' to record input tax credit (ITC).",
            severity: "error",
            category: "duties_and_taxes",
            impact: "Blocks posting of intra-state GST Purchase Invoices.",
            canAutoRepair: true,
            proposedActions: missingActions
          });
        }
      }

      if ((isInterState && igstAmount > 0) || !igstIn) {
        if (!igstIn) {
          issues.push({
            id: "err-missing-igst-in",
            code: "MISSING_IGST_INPUT_LEDGER",
            title: "Missing IGST Input Tax Ledger in Duties & Taxes",
            description: "Inter-state purchases require an IGST Input ledger under 'Duties & Taxes' to record integrated input tax credit.",
            severity: "error",
            category: "duties_and_taxes",
            impact: "Blocks posting of inter-state GST Purchase Invoices.",
            canAutoRepair: true,
            proposedActions: [{
              action: "create_ledger",
              ledgerName: STANDARD_REQUIRED_LEDGERS.IGST_IN.name,
              group: STANDARD_REQUIRED_LEDGERS.IGST_IN.group,
              nature: STANDARD_REQUIRED_LEDGERS.IGST_IN.nature,
              type: STANDARD_REQUIRED_LEDGERS.IGST_IN.type,
              openingBalance: 0,
              reason: "Integrated GST Input Tax Credit ledger under Duties & Taxes (Asset / Dr)"
            }]
          });
        }
      }
    } else if (isSales) {
      // Sales requires Output Tax Ledgers (Liability / Cr)
      const cgstOut = findMatchingLedger(ledgers, STANDARD_REQUIRED_LEDGERS.CGST_OUT);
      const sgstOut = findMatchingLedger(ledgers, STANDARD_REQUIRED_LEDGERS.SGST_OUT);
      const igstOut = findMatchingLedger(ledgers, STANDARD_REQUIRED_LEDGERS.IGST_OUT);

      if ((!isInterState && (cgstAmount > 0 || sgstAmount > 0)) || (!cgstOut || !sgstOut)) {
        if (!cgstOut || !sgstOut) {
          const missingActions: DiagnosticProposedAction[] = [];
          if (!cgstOut) {
            missingActions.push({
              action: "create_ledger",
              ledgerName: STANDARD_REQUIRED_LEDGERS.CGST_OUT.name,
              group: STANDARD_REQUIRED_LEDGERS.CGST_OUT.group,
              nature: STANDARD_REQUIRED_LEDGERS.CGST_OUT.nature,
              type: STANDARD_REQUIRED_LEDGERS.CGST_OUT.type,
              openingBalance: 0,
              reason: "Central GST Output Tax Liability ledger under Duties & Taxes (Liability / Cr)"
            });
          }
          if (!sgstOut) {
            missingActions.push({
              action: "create_ledger",
              ledgerName: STANDARD_REQUIRED_LEDGERS.SGST_OUT.name,
              group: STANDARD_REQUIRED_LEDGERS.SGST_OUT.group,
              nature: STANDARD_REQUIRED_LEDGERS.SGST_OUT.nature,
              type: STANDARD_REQUIRED_LEDGERS.SGST_OUT.type,
              openingBalance: 0,
              reason: "State GST Output Tax Liability ledger under Duties & Taxes (Liability / Cr)"
            });
          }

          issues.push({
            id: "err-missing-cgst-sgst-out",
            code: "MISSING_CGST_SGST_OUTPUT_LEDGER",
            title: "Missing CGST / SGST Output Tax Ledgers in Duties & Taxes",
            description: "Intra-state sales require CGST and SGST Output ledgers under 'Duties & Taxes' to record tax liability collected from customers.",
            severity: "error",
            category: "duties_and_taxes",
            impact: "Blocks posting of intra-state GST Sales Invoices.",
            canAutoRepair: true,
            proposedActions: missingActions
          });
        }
      }

      if ((isInterState && igstAmount > 0) || !igstOut) {
        if (!igstOut) {
          issues.push({
            id: "err-missing-igst-out",
            code: "MISSING_IGST_OUTPUT_LEDGER",
            title: "Missing IGST Output Tax Ledger in Duties & Taxes",
            description: "Inter-state sales require an IGST Output ledger under 'Duties & Taxes' to record integrated tax liability.",
            severity: "error",
            category: "duties_and_taxes",
            impact: "Blocks posting of inter-state GST Sales Invoices.",
            canAutoRepair: true,
            proposedActions: [{
              action: "create_ledger",
              ledgerName: STANDARD_REQUIRED_LEDGERS.IGST_OUT.name,
              group: STANDARD_REQUIRED_LEDGERS.IGST_OUT.group,
              nature: STANDARD_REQUIRED_LEDGERS.IGST_OUT.nature,
              type: STANDARD_REQUIRED_LEDGERS.IGST_OUT.type,
              openingBalance: 0,
              reason: "Integrated GST Output Tax Liability ledger under Duties & Taxes (Liability / Cr)"
            }]
          });
        }
      }
    }
  }

  // 3. Round Off Ledger Check
  const roundOff = findMatchingLedger(ledgers, STANDARD_REQUIRED_LEDGERS.ROUNDOFF);
  if (!roundOff) {
    issues.push({
      id: "warn-missing-roundoff",
      code: "MISSING_ROUNDOFF_ACCOUNT",
      title: "Missing Round Off Ledger",
      description: "No Round Off ledger found in 'Indirect Expenses'. Invoices with paisa rounding will create fractional ledger differences.",
      severity: "warning",
      category: "sales_purchase",
      impact: "May cause minor round-off imbalances on large invoices.",
      canAutoRepair: true,
      proposedActions: [{
        action: "create_ledger",
        ledgerName: STANDARD_REQUIRED_LEDGERS.ROUNDOFF.name,
        group: STANDARD_REQUIRED_LEDGERS.ROUNDOFF.group,
        nature: STANDARD_REQUIRED_LEDGERS.ROUNDOFF.nature,
        type: STANDARD_REQUIRED_LEDGERS.ROUNDOFF.type,
        openingBalance: 0,
        reason: "Invoice fractional round-off adjustment ledger under Indirect Expenses"
      }]
    });
  }

  const hasErrors = issues.some(i => i.severity === "error");
  const autoRepairableCount = issues.filter(i => i.canAutoRepair).length;

  return {
    companyId,
    companyName,
    isGstRegistered,
    hasErrors,
    issues,
    autoRepairableCount,
    summary: hasErrors
      ? `Found ${issues.length} configuration issues (${issues.filter(i => i.severity === 'error').length} blocking errors) preventing invoice posting.`
      : issues.length > 0
      ? `Configuration is valid with ${issues.length} optional recommendation(s).`
      : "Accounting configuration is 100% compliant."
  };
}

/**
 * Diagnoses the complete Chart of Accounts for a company
 */
export function diagnoseCompanyChart(company: Company | null, ledgers: Ledger[]): DiagnosticReport {
  const companyId = company?.id || "";
  const companyName = company?.name || "Active Company";
  const isGstRegistered = Boolean(company?.gstRegistered || company?.gstin);
  const issues: DiagnosticIssue[] = [];

  // Check Core Liquid Accounts
  const cash = findMatchingLedger(ledgers, STANDARD_REQUIRED_LEDGERS.CASH);
  if (!cash) {
    issues.push({
      id: "err-missing-cash",
      code: "MISSING_CASH_ACCOUNT",
      title: "Missing Cash-in-Hand Account",
      description: "No cash account found under 'Cash-in-Hand'. Required for cash sales, purchases, and contra vouchers.",
      severity: "error",
      category: "liquid_accounts",
      impact: "Cannot record cash vouchers or cash register entries.",
      canAutoRepair: true,
      proposedActions: [{
        action: "create_ledger",
        ledgerName: STANDARD_REQUIRED_LEDGERS.CASH.name,
        group: STANDARD_REQUIRED_LEDGERS.CASH.group,
        nature: STANDARD_REQUIRED_LEDGERS.CASH.nature,
        type: STANDARD_REQUIRED_LEDGERS.CASH.type,
        openingBalance: 0,
        reason: "Primary liquid cash account"
      }]
    });
  }

  const bank = findMatchingLedger(ledgers, STANDARD_REQUIRED_LEDGERS.BANK);
  if (!bank) {
    issues.push({
      id: "warn-missing-bank",
      code: "MISSING_BANK_ACCOUNT",
      title: "Missing Bank Account",
      description: "No bank account found under 'Bank Accounts'. Required for NEFT/RTGS/UPI and cheque transactions.",
      severity: "warning",
      category: "liquid_accounts",
      impact: "Cannot reconcile bank statements or post bank vouchers.",
      canAutoRepair: true,
      proposedActions: [{
        action: "create_ledger",
        ledgerName: STANDARD_REQUIRED_LEDGERS.BANK.name,
        group: STANDARD_REQUIRED_LEDGERS.BANK.group,
        nature: STANDARD_REQUIRED_LEDGERS.BANK.nature,
        type: STANDARD_REQUIRED_LEDGERS.BANK.type,
        openingBalance: 0,
        reason: "Default business bank current account"
      }]
    });
  }

  // Check Sales & Purchase Accounts
  const sales = findMatchingLedger(ledgers, STANDARD_REQUIRED_LEDGERS.SALES);
  if (!sales) {
    issues.push({
      id: "err-missing-sales",
      code: "MISSING_SALES_ACCOUNT",
      title: "Missing Sales Account",
      description: "No standard Sales Account under 'Sales Accounts'.",
      severity: "error",
      category: "sales_purchase",
      impact: "Cannot post sales transactions.",
      canAutoRepair: true,
      proposedActions: [{
        action: "create_ledger",
        ledgerName: STANDARD_REQUIRED_LEDGERS.SALES.name,
        group: STANDARD_REQUIRED_LEDGERS.SALES.group,
        nature: STANDARD_REQUIRED_LEDGERS.SALES.nature,
        type: STANDARD_REQUIRED_LEDGERS.SALES.type,
        openingBalance: 0,
        reason: "Standard trading sales revenue account"
      }]
    });
  }

  const purchase = findMatchingLedger(ledgers, STANDARD_REQUIRED_LEDGERS.PURCHASE);
  if (!purchase) {
    issues.push({
      id: "err-missing-purchase",
      code: "MISSING_PURCHASE_ACCOUNT",
      title: "Missing Purchase Account",
      description: "No standard Purchase Account under 'Purchase Accounts'.",
      severity: "error",
      category: "sales_purchase",
      impact: "Cannot post vendor bills and purchase entries.",
      canAutoRepair: true,
      proposedActions: [{
        action: "create_ledger",
        ledgerName: STANDARD_REQUIRED_LEDGERS.PURCHASE.name,
        group: STANDARD_REQUIRED_LEDGERS.PURCHASE.group,
        nature: STANDARD_REQUIRED_LEDGERS.PURCHASE.nature,
        type: STANDARD_REQUIRED_LEDGERS.PURCHASE.type,
        openingBalance: 0,
        reason: "Standard trading purchase account"
      }]
    });
  }

  // If GST Registered, check all 6 statutory GST ledgers
  if (isGstRegistered) {
    const gstSpecs = [
      STANDARD_REQUIRED_LEDGERS.CGST_IN,
      STANDARD_REQUIRED_LEDGERS.SGST_IN,
      STANDARD_REQUIRED_LEDGERS.IGST_IN,
      STANDARD_REQUIRED_LEDGERS.CGST_OUT,
      STANDARD_REQUIRED_LEDGERS.SGST_OUT,
      STANDARD_REQUIRED_LEDGERS.IGST_OUT,
    ];

    const missingGstActions: DiagnosticProposedAction[] = [];

    for (const spec of gstSpecs) {
      const match = findMatchingLedger(ledgers, spec);
      if (!match) {
        missingGstActions.push({
          action: "create_ledger",
          ledgerName: spec.name,
          group: spec.group,
          nature: spec.nature,
          type: spec.type,
          openingBalance: 0,
          reason: spec.description
        });
      }
    }

    if (missingGstActions.length > 0) {
      issues.push({
        id: "err-missing-gst-statutory",
        code: "MISSING_STATUTORY_GST_LEDGERS",
        title: `Missing ${missingGstActions.length} Statutory GST Ledgers`,
        description: `This GST-registered business is missing required input/output tax ledgers in 'Duties & Taxes'.`,
        severity: "error",
        category: "duties_and_taxes",
        impact: "Blocks GST sales and purchase invoice generation.",
        canAutoRepair: true,
        proposedActions: missingGstActions
      });
    }
  }

  // Check for Misgrouped Ledgers (e.g. CGST under Expenses or Sales under Assets)
  for (const l of ledgers) {
    const lower = l.name.toLowerCase();
    if ((lower.includes("cgst") || lower.includes("sgst") || lower.includes("igst")) && l.group !== "Duties & Taxes") {
      issues.push({
        id: `warn-misgrouped-gst-${l.id}`,
        code: "MISGROUPED_GST_LEDGER",
        title: `Misgrouped GST Ledger: ${l.name}`,
        description: `Ledger '${l.name}' is categorized under '${l.group}' instead of 'Duties & Taxes'. Tax calculations will fail to isolate statutory liabilities.`,
        severity: "warning",
        category: "group_mapping",
        impact: "Distorts Balance Sheet and GSTR reconciliation.",
        canAutoRepair: true,
        proposedActions: [{
          action: "update_ledger_group",
          ledgerId: l.id,
          ledgerName: l.name,
          group: "Duties & Taxes",
          nature: lower.includes("in") ? "Asset" : "Liability",
          type: lower.includes("in") ? "Dr" : "Cr",
          openingBalance: l.openingBalance || 0,
          reason: "Relocate statutory tax ledger to Duties & Taxes group"
        }]
      });
    }
  }

  const hasErrors = issues.some(i => i.severity === "error");
  const autoRepairableCount = issues.filter(i => i.canAutoRepair).length;

  return {
    companyId,
    companyName,
    isGstRegistered,
    hasErrors,
    issues,
    autoRepairableCount,
    summary: hasErrors
      ? `Chart of accounts requires repair: ${issues.filter(i => i.severity === 'error').length} blocking errors detected.`
      : issues.length > 0
      ? `Chart of accounts is operational with ${issues.length} configuration recommendations.`
      : "Chart of Accounts is fully compliant."
  };
}
